<ng-container *ngIf="visible">

    <!--
        The closed state: a tab on the right edge of the viewport.

        The tooltip hangs off the inner control, not the button. Material centres a tooltip on its
        host, and the button is the full height of the page — so on the button it appeared halfway
        down an empty rail, nowhere near the icon it describes.
    -->
    <button *ngIf="!isOpen" type="button" class="insights-tab" (click)="open()"
        aria-label="Open performance insights">
        <span class="insights-tab__control"
            matTooltip="View estimated campaign performance and audience insights."
            matTooltipPosition="left">
            <span class="insights-tab__chevron">&rsaquo;</span>
            <img class="insights-tab__icon" src="./assets/images/icons/nav/analytics.svg" alt="" width="28" />
        </span>
    </button>

    <div *ngIf="isOpen" class="insights-panel">

        <div class="insights-panel__header">
            <h6 class="insights-panel__title montserrat-semi-bold mb-0">
                PERFORMANCE INSIGHTS
                <span class="insights-panel__info"
                    matTooltip="View estimated campaign performance and audience insights."
                    matTooltipPosition="below">&#9432;</span>
            </h6>
            <!--
                The two halves of the exchange behind these figures, each copied on its own.

                The response settles whether the provider's numbers are right; the request settles
                whether TMC asked the right question — the spot length, play rate and booked hours
                decide every figure that comes back and appear nowhere in the response. TMC does not
                show either untouched (reach arrives as a percentage and is shown as a headcount,
                GRP is not returned at all), so these are what trace a disputed number to its source.
            -->
            <span *ngIf="hasRawData" class="insights-panel__copies">
                <button type="button" class="insights-panel__copy" (click)="copyRawData('request')"
                    [matTooltip]="'Copy what TMC asked the provider for these figures.'"
                    matTooltipPosition="below">
                    {{ copied === 'request' ? 'Copied' : 'Copy request' }}
                </button>
                <button type="button" class="insights-panel__copy" (click)="copyRawData('response')"
                    [matTooltip]="'Copy the provider\'s raw responses behind these figures.'"
                    matTooltipPosition="below">
                    {{ copied === 'response' ? 'Copied' : 'Copy response' }}
                </button>
            </span>
            <button type="button" class="insights-panel__close" (click)="close()"
                aria-label="Close performance insights">&times;</button>
        </div>

        <div class="insights-panel__body">

            <!--
                The app's own spinner. Its circle is stroked with $primary globally
                (assets/base/_base.scss), which is what makes it the yellow loader used elsewhere —
                so no colour is set here.
            -->
            <ng-container *ngIf="isLoading">
                <div class="insights-loading">
                    <mat-spinner diameter="48"></mat-spinner>
                </div>
            </ng-container>

            <ng-container *ngIf="!isLoading && hasFailed">
                <p class="text-small text-center my-4">
                    Insights are unavailable right now. Please try again in a moment.
                </p>
            </ng-container>

            <!--
                The empty state. Reached before anything is booked, which on the scheduling step is
                where the client starts — so it says what to do rather than that nothing is here.

                The chart is inline rather than an asset: it is three bars and a badge, and drawing
                it here keeps it in step with the panel's own colours instead of shipping another
                file to keep in sync.
            -->
            <ng-container *ngIf="!isLoading && !hasFailed && !insights">
                <div class="insights-empty">
                    <svg class="insights-empty__art" width="150" height="120"
                        viewBox="0 0 150 120" role="img" aria-label="No insights yet"
                        focusable="false">
                        <rect x="28" y="56" width="24" height="48" rx="8" fill="#e5e8ee" />
                        <rect x="58" y="36" width="24" height="68" rx="8" fill="#eef0f4" />
                        <rect x="88" y="18" width="24" height="86" rx="8" fill="#e5e8ee" />
                        <rect x="22" y="108" width="96" height="7" rx="3.5" fill="#e8ebf0" />
                        <circle cx="116" cy="38" r="15.5" fill="#fde150" />
                        <circle cx="116" cy="31.98" r="1.89" fill="#fff" />
                        <rect x="114.36" y="35.08" width="3.27" height="10.83" rx="1.635" fill="#fff" />
                    </svg>
                    <!-- Booked slots the provider has no forecast for (a screen or dates it does not
                         cover) are not an unmade selection, so they get their own message. -->
                    <ng-container *ngIf="noForecast; else selectPrompt">
                        <p class="insights-empty__title montserrat-semi-bold">
                            No forecast is available for this selection.
                        </p>
                        <p class="insights-empty__body mb-0">
                            The audience provider has no performance data for the selected channels
                            on these dates.
                        </p>
                    </ng-container>
                    <ng-template #selectPrompt>
                        <p class="insights-empty__title montserrat-semi-bold">
                            Select channels and slots to view performance insights.
                        </p>
                        <p class="insights-empty__body mb-0">
                            Your performance insights will appear here once you’ve selected at least one
                            channel and one slot.
                        </p>
                    </ng-template>
                </div>
            </ng-container>

            <ng-container *ngIf="!isLoading && insights">

                <!--
                    Every section below compares the channels that have at least one booked slot.
                    There is no channel picker: a channel earns its row by being booked, and loses
                    it when its last slot goes.
                -->

                <!-- Performance -->
                <section class="insights-section">
                    <h6 class="insights-section__title montserrat-semi-bold">
                        PERFORMANCE TABLE
                        <span class="insights-section__info"
                            matTooltip="Impressions, reach and GRP for each booked channel."
                            matTooltipPosition="below">&#9432;</span>
                    </h6>

                    <div class="insights-table-wrap">
                        <table class="table table-sm insights-table mb-0">
                            <thead>
                                <tr>
                                    <th>Channel</th>
                                    <th class="text-right">Impressions</th>
                                    <th class="text-right">Reach</th>
                                    <th class="text-right">GRP</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let row of channels">
                                    <!-- Clamped with the full name on hover: a screen's name can be
                                         a whole street address, and left to wrap it drove one row to
                                         eight lines and pushed the figures out of view. -->
                                    <td class="insights-table__name" [title]="row.label">{{ row.label }}</td>
                                    <td class="text-right">{{ row.impressions | number }}</td>
                                    <!-- A dash, not a zero: the provider does not always break reach
                                         down per screen, and nobody-reached is a different claim. -->
                                    <td class="text-right">
                                        {{ row.reach == null ? '—' : (row.reach | number) }}
                                    </td>
                                    <td class="text-right">{{ row.grp | number: '1.1-1' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <!-- Targeted population -->
                <section class="insights-section" *ngIf="cities.length">
                    <h6 class="insights-section__title montserrat-semi-bold">
                        TARGETED POPULATION
                        <span class="insights-section__info"
                            matTooltip="The target audience size in each city the campaign runs in."
                            matTooltipPosition="below">&#9432;</span>
                    </h6>

                    <div class="insights-table-wrap">
                        <table class="table table-sm insights-table mb-0">
                            <thead>
                                <tr>
                                    <th>City</th>
                                    <th class="text-right">Targeted population</th>
                                </tr>
                            </thead>
                            <tbody>
                                <!-- One row per city, however many of the campaign's channels
                                     stand in it: the population is the city's, not each screen's,
                                     and repeating it per channel would read as more people. -->
                                <tr *ngFor="let city of cities">
                                    <td>{{ city.label }}</td>
                                    <td class="text-right">{{ city.population | number }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <!--
                    The three comparison tables below put the channels down the side and the bands
                    across the top, so one band reads across every channel at a glance. Each scrolls
                    sideways on its own — the frequency ladder in particular can run to eighteen
                    steps, and the drawer is narrow.
                -->

                <!-- Frequency, per channel -->
                <section class="insights-section" *ngIf="frequencyColumns.length">
                    <h6 class="insights-section__title montserrat-semi-bold">
                        FREQUENCY DISTRIBUTION
                        <span class="insights-section__scope">(by Channel)</span>
                        <span class="insights-section__info"
                            matTooltip="How many people each channel reaches at least n times."
                            matTooltipPosition="below">&#9432;</span>
                    </h6>

                    <div class="insights-table-wrap insights-table-wrap--scroll">
                        <table class="table table-sm insights-table insights-matrix mb-0">
                            <thead>
                                <tr>
                                    <th class="insights-matrix__corner">Channel</th>
                                    <th class="text-right" *ngFor="let column of frequencyColumns">
                                        {{ column }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let channel of audienceRows">
                                    <td class="insights-table__name" [title]="channel.label">
                                        {{ channel.label }}
                                    </td>
                                    <td class="text-right" *ngFor="let column of frequencyColumns">
                                        <ng-container
                                            *ngIf="cellFor(channel.frequencyDistribution, column) as cell;
                                                   else noCell">
                                            <span class="insights-cell__value">
                                                {{ cell.audience | number }}
                                            </span>
                                            <span class="insights-cell__share">
                                                {{ cell.percentOfAudience | number: '1.0-1' }}%
                                            </span>
                                        </ng-container>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <p class="insights-note text-small mb-0">
                        “2+” is the share of this channel’s targeted population expected to see the
                        campaign at least twice. Each row is that channel alone.
                    </p>
                </section>

                <!-- Gender, per channel -->
                <section class="insights-section" *ngIf="genderColumns.length">
                    <h6 class="insights-section__title montserrat-semi-bold">
                        IMPRESSIONS BY GENDER
                        <span class="insights-section__scope">(by Channel)</span>
                        <span class="insights-section__info"
                            matTooltip="Each channel's impressions split by gender."
                            matTooltipPosition="below">&#9432;</span>
                    </h6>

                    <div class="insights-table-wrap insights-table-wrap--scroll">
                        <table class="table table-sm insights-table insights-matrix mb-0">
                            <thead>
                                <tr>
                                    <th class="insights-matrix__corner">Channel</th>
                                    <th class="text-right" *ngFor="let column of genderColumns">
                                        {{ column }}
                                    </th>
                                    <th class="text-right">Total Impressions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let channel of audienceRows">
                                    <td class="insights-table__name" [title]="channel.label">
                                        {{ channel.label }}
                                    </td>
                                    <td class="text-right" *ngFor="let column of genderColumns">
                                        <ng-container
                                            *ngIf="cellFor(channel.genderDistribution, column) as cell;
                                                   else noCell">
                                            <span class="insights-cell__value">
                                                {{ cell.impressions | number }}
                                            </span>
                                            <span class="insights-cell__share">
                                                {{ cell.percentOfAudience | number: '1.0-1' }}%
                                            </span>
                                        </ng-container>
                                    </td>
                                    <td class="text-right">
                                        {{ impressionsOf(channel.channelId) | number }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Reach is deliberately absent: the provider reports none per slice, and a
                         zero there would read as a slice nobody saw. -->
                </section>

                <!-- Age, per channel -->
                <section class="insights-section" *ngIf="ageColumns.length">
                    <h6 class="insights-section__title montserrat-semi-bold">
                        IMPRESSIONS BY AGE GROUP
                        <span class="insights-section__scope">(by Channel)</span>
                        <span class="insights-section__info"
                            matTooltip="Each channel's impressions split by age group."
                            matTooltipPosition="below">&#9432;</span>
                    </h6>

                    <div class="insights-table-wrap insights-table-wrap--scroll">
                        <table class="table table-sm insights-table insights-matrix mb-0">
                            <thead>
                                <tr>
                                    <th class="insights-matrix__corner">Channel</th>
                                    <th class="text-right" *ngFor="let column of ageColumns">
                                        {{ column }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let channel of audienceRows">
                                    <td class="insights-table__name" [title]="channel.label">
                                        {{ channel.label }}
                                    </td>
                                    <td class="text-right" *ngFor="let column of ageColumns">
                                        <ng-container
                                            *ngIf="cellFor(channel.ageDistribution, column) as cell;
                                                   else noCell">
                                            <span class="insights-cell__value">
                                                {{ cell.impressions | number }}
                                            </span>
                                            <span class="insights-cell__share">
                                                {{ cell.percentOfAudience | number: '1.0-1' }}%
                                            </span>
                                        </ng-container>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <p class="insights-note text-small mb-0">
                        The provider's own bands. They total less than the channel's impressions
                        where it reported nothing for a band.
                    </p>
                </section>

                <!-- A band the provider said nothing about for this channel. A dash, never a zero:
                     unreported is not the same as reached nobody. -->
                <ng-template #noCell><span class="insights-cell__value">—</span></ng-template>

            </ng-container>
        </div>

    </div>
</ng-container>